import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";

export default function Privacy() {
  return (
    <div data-testid="page-privacy" className="pt-[96px]">
      <SeoHead
        title="Privacybeleid — SovEU"
        description="Lees hoe SovEU omgaat met persoonsgegevens, contactgegevens en gegevensverwerking."
        path="/privacybeleid"
      />

      {/* Hero / intro */}
      <section className="section bg-white">
        <div className="container-soveu max-w-[860px]">
          <FadeIn>
            <div className="eyebrow mb-4">Juridisch</div>
            <h1 className="h1-display mb-5">Privacybeleid SovEU</h1>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[14px] text-[#6B7280]">
              <div><span className="font-medium text-[#1E3A5F]">Versie:</span> mei 2026</div>
              <div><span className="font-medium text-[#1E3A5F]">Website:</span> soveu.nl</div>
              <div>
                <span className="font-medium text-[#1E3A5F]">Contact:</span>{" "}
                <a href="mailto:info@soveu.nl" className="underline">info@soveu.nl</a>
              </div>
              <div><span className="font-medium text-[#1E3A5F]">KVK:</span> 42033105</div>
            </dl>
          </FadeIn>
        </div>
      </section>

      {/* Body */}
      <article className="pb-16">
        <div className="container-soveu max-w-[860px] prose-soveu">
          <FadeIn>
            <Section number="1" title="Inleiding">
              <p>
                SovEU (gevestigd te Utrecht, KVK 42033105) neemt uw privacy serieus. In dit document leggen wij uit welke persoonsgegevens wij verzamelen, waarom wij dat doen en hoe wij zorgen dat uw gegevens veilig worden verwerkt in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG).
              </p>
            </Section>

            <Section number="2" title="Welke persoonsgegevens verwerken wij?">
              <p>
                Omdat SovEU zich richt op B2B-adviesdiensten, verwerken wij voornamelijk zakelijke persoonsgegevens. Wij verwerken de volgende gegevens wanneer u gebruikmaakt van onze diensten of via onze website (soveu.nl) contact met ons opneemt:
              </p>
              <ul>
                <li>Voor- en achternaam</li>
                <li>Functietitel en organisatie</li>
                <li>Zakelijk e-mailadres</li>
                <li>Zakelijk telefoonnummer</li>
                <li>Inhoud van berichten die u via het contactformulier of per e-mail achterlaat</li>
                <li>Gegevens over uw activiteiten op onze website (functionele of analytische data zonder herleidbaarheid tot individuen)</li>
              </ul>
              <p className="callout">
                <strong>Let op:</strong> In de uitvoering van onze dienstverlening (zoals de Bestuursscan of het Regiebeeld) verwerken wij primair bedrijfs- en systeeminformatie. SovEU zal nooit doelbewust toegang vragen tot daadwerkelijke inhoudelijke databases met persoonsgegevens van uw burgers, huurders of cliënten, tenzij dit in een uiterst uitzonderlijk geval noodzakelijk is voor het advies en daarvoor een separate verwerkersovereenkomst is afgesloten.
              </p>
            </Section>

            <Section number="3" title="Met welk doel en op basis van welke grondslag verwerken wij persoonsgegevens?">
              <p>SovEU verwerkt uw persoonsgegevens voor de volgende doelen:</p>
              <ul>
                <li>Om u te kunnen bellen of e-mailen indien dit nodig is om onze dienstverlening uit te kunnen voeren <em>(grondslag: uitvoering van een overeenkomst / gerechtvaardigd belang)</em>.</li>
                <li>Het afhandelen van uw betalingen en facturatie <em>(grondslag: wettelijke verplichting / uitvoering overeenkomst)</em>.</li>
                <li>Het verzenden van analyses, rapporten of gerichte informatie <em>(grondslag: toestemming / uitvoering overeenkomst)</em>.</li>
              </ul>
            </Section>

            <Section number="4" title="Hoe lang bewaren wij persoonsgegevens?">
              <p>SovEU bewaart uw persoonsgegevens niet langer dan strikt nodig is om de doelen te realiseren waarvoor uw gegevens worden verzameld.</p>
              <ul>
                <li>Administratieve en financiële gegevens (zoals facturen met contactnamen) worden 7 jaar bewaard ter voldoening aan de wettelijke bewaarplicht van de Belastingdienst.</li>
                <li>Klantdossiers en correspondentie worden bewaard zolang u klant bent, of tot maximaal 2 jaar na het laatste contact, tenzij anders overeengekomen.</li>
                <li>Contactformulier-inzendingen worden uiterlijk na 1 jaar verwijderd indien hier geen zakelijke overeenkomst uit volgt.</li>
              </ul>
            </Section>

            <Section number="5" title="Delen van persoonsgegevens met derden">
              <p>
                SovEU verkoopt uw gegevens niet aan derden en verstrekt deze uitsluitend indien dit nodig is voor de uitvoering van onze overeenkomst met u of om te voldoen aan een wettelijke verplichting. Met bedrijven die uw gegevens verwerken in onze opdracht (zoals onze boekhoudsoftware of e-mailhosting), sluiten wij verwerkersovereenkomsten om te zorgen voor eenzelfde niveau van beveiliging en vertrouwelijkheid. SovEU blijft verantwoordelijk voor deze verwerkingen.
              </p>
            </Section>

            <Section number="6" title="Gegevens inzien, aanpassen of verwijderen">
              <p>
                U heeft het recht om uw persoonsgegevens in te zien, te corrigeren of te verwijderen. Daarnaast heeft u het recht om uw eventuele toestemming voor de gegevensverwerking in te trekken of bezwaar te maken tegen de verwerking van uw persoonsgegevens door SovEU. U kunt een verzoek tot inzage, correctie, verwijdering of bezwaar sturen naar{" "}
                <a href="mailto:info@soveu.nl">info@soveu.nl</a>. Wij reageren zo snel mogelijk, maar uiterlijk binnen vier weken, op uw verzoek.
              </p>
            </Section>

            <Section number="7" title="Hoe wij persoonsgegevens beveiligen">
              <p>
                SovEU neemt de bescherming van uw gegevens serieus en neemt passende technische en organisatorische maatregelen om misbruik, verlies, onbevoegde toegang, ongewenste openbaarmaking en ongeoorloofde wijziging tegen te gaan. Als u de indruk heeft dat uw gegevens niet goed beveiligd zijn of er aanwijzingen zijn van misbruik, neem dan direct contact op via{" "}
                <a href="mailto:info@soveu.nl">info@soveu.nl</a>.
              </p>
            </Section>

            <Section number="8" title="Cookies">
              <p>
                De website soveu.nl gebruikt uitsluitend functionele cookies en vergelijkbare technieken die strikt noodzakelijk zijn voor een goede werking van de website. SovEU plaatst geen tracking cookies, geen advertentiecookies en geen analytische cookies die individuele bezoekers herleidbaar maken. Wij gebruiken geen Google Analytics, geen pixels van sociale netwerken en geen externe trackers.
              </p>
              <p>
                Mocht in de toekomst gebruik worden gemaakt van analytische cookies, dan zal dat uitsluitend gebeuren met uw voorafgaande, geïnformeerde toestemming en in overeenstemming met de Telecommunicatiewet en de AVG.
              </p>
            </Section>

            <Section number="9" title="Wijzigingen van dit privacybeleid">
              <p>
                SovEU kan dit privacybeleid aanpassen. Wij plaatsen wijzigingen op deze pagina en passen de versiedatum bovenaan aan. Wij raden u aan dit beleid periodiek te raadplegen.
              </p>
            </Section>

            <Section number="10" title="Contact">
              <p>
                Heeft u vragen over dit privacybeleid of over de manier waarop SovEU met persoonsgegevens omgaat? Neem dan contact op via{" "}
                <a href="mailto:info@soveu.nl">info@soveu.nl</a>.
              </p>
            </Section>
          </FadeIn>
        </div>
      </article>

      {/* CTA */}
      <section className="section" style={{ backgroundColor: "#F5F7FA" }} data-testid="privacy-cta">
        <div className="container-soveu max-w-[860px]">
          <FadeIn>
            <div className="soveu-card p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <h2 className="h3-display mb-2">Heeft u vragen over dit privacybeleid?</h2>
                <p className="text-[#334155] text-[15px]">
                  Neem contact op via <a href="mailto:info@soveu.nl" className="underline" style={{ color: "#1E3A5F" }}>info@soveu.nl</a>.
                </p>
              </div>
              <a
                href="mailto:info@soveu.nl"
                className="btn-primary"
                data-testid="privacy-mail-cta"
              >
                <Mail size={16} />
                Mail SovEU
              </a>
            </div>
            <p className="text-[13px] text-[#6B7280] mt-6 text-center">
              Zie ook onze{" "}
              <Link to="/algemene-voorwaarden" className="underline" style={{ color: "#1E3A5F" }}>
                algemene voorwaarden
              </Link>.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="legal-section" data-testid={`privacy-section-${number}`}>
      <h2>
        <span className="legal-num">{number}.</span> {title}
      </h2>
      {children}
    </section>
  );
}
